import { useAtom } from 'jotai';
import { useCallback } from 'react';

import { type Theme, themeAtom } from '../atoms/themeAtom';

/**
 * Hook de acesso e controle do tema da aplicação.
 *
 * Encapsula o átomo `themeAtom` — componentes nunca devem usar o átomo diretamente.
 *
 * @returns `theme` — tema atual; `setTheme` — definir tema específico; `toggle` — alternar
 *
 * @example
 * ```tsx
 * function ThemeToggle() {
 *   const { theme, toggle } = useTheme();
 *   return (
 *     <button onClick={toggle} aria-label="Alternar tema">
 *       {theme === 'light' ? '🌙' : '☀️'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useTheme() {
  const [theme, setTheme] = useAtom(themeAtom);

  const toggle = useCallback(
    () => setTheme(current => (current === 'light' ? 'dark' : 'light')),
    [setTheme],
  );

  const setSpecificTheme = useCallback((newTheme: Theme) => setTheme(newTheme), [setTheme]);

  return { theme, setTheme: setSpecificTheme, toggle } as const;
}
